import { useState, type ReactNode } from 'react';
import { IonIcon } from '@ionic/react';
import { cameraOutline } from 'ionicons/icons';
import type { PhotoResult } from '../../../hooks/usePhotoGallery';
import { usePhotoPicker } from '../../../hooks/usePhotoPicker';
import { useErrorToast } from '../../../hooks/useErrorToast';
import { cn } from '../../../lib/utils';
import { PhotoSourceModal } from './PhotoSourceModal';

/** Payload errors returned by an upload/delete mutation (empty when it succeeded). */
type MutationErrors = readonly string[] | null | undefined;

interface EditableImageProps {
  /** The current image (or its fallback), shown while no mutation is running. */
  children: ReactNode;
  /** Shown instead of `children` while `loading`. */
  loadingContent: ReactNode;
  loading: boolean;
  /** Whether there is an image to delete: only then the delete option is offered. */
  hasImage: boolean;
  /** Runs the upload mutation and returns its payload errors. */
  onUpload: (photo: PhotoResult) => Promise<MutationErrors>;
  /** Runs the delete mutation and returns its payload errors. */
  onDelete: () => Promise<MutationErrors>;
  modalTitle: string;
  deleteLabel?: string;
  /** Accessible label of the camera button. */
  editLabel: string;
  uploadErrorMessage?: string;
  deleteErrorMessage?: string;
  className?: string;
  buttonClassName?: string;
  iconClassName?: string;
  buttonTestId?: string;
}

/**
 * An image with a camera badge that opens the photo source modal (camera,
 * gallery, delete). Shared by every editable picture in the app (profile
 * picture, league image, channel image): callers provide the image and run
 * their own mutations, this component picks the photo and reports failures.
 */
export const EditableImage = ({
  children,
  loadingContent,
  loading,
  hasImage,
  onUpload,
  onDelete,
  modalTitle,
  deleteLabel,
  editLabel,
  uploadErrorMessage = 'No se pudo subir la imagen. Inténtalo de nuevo.',
  deleteErrorMessage = 'No se pudo eliminar la imagen. Inténtalo de nuevo.',
  className,
  buttonClassName,
  iconClassName,
  buttonTestId,
}: EditableImageProps) => {
  const [showModal, setShowModal] = useState(false);
  const showError = useErrorToast();
  const { pickFromCamera, pickFromGallery } = usePhotoPicker();

  const run = async (mutation: () => Promise<MutationErrors>, errorMessage: string) => {
    try {
      const errors = (await mutation()) ?? [];
      if (errors.length > 0) showError(errors[0]);
    } catch {
      showError(errorMessage);
    }
  };

  const handleUpload = (photo: PhotoResult) => run(() => onUpload(photo), uploadErrorMessage);

  const handleDelete = async () => {
    setShowModal(false);
    await run(onDelete, deleteErrorMessage);
  };

  return (
    <>
      <div className={cn('relative shrink-0', className)}>
        {loading ? loadingContent : children}
        <button
          className={cn(
            'absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center bg-white shadow-md border border-gray-200 disabled:opacity-50 cursor-pointer transition-transform active:scale-90',
            buttonClassName
          )}
          // Inline so global button styles cannot override the round badge.
          style={{ borderRadius: '9999px', padding: 0 }}
          onClick={() => setShowModal(true)}
          disabled={loading}
          data-testid={buttonTestId}
          aria-label={editLabel}
        >
          <IonIcon icon={cameraOutline} className={cn('text-gray-700 text-sm', iconClassName)} />
        </button>
      </div>

      <PhotoSourceModal
        isOpen={showModal}
        title={modalTitle}
        deleteLabel={deleteLabel}
        onTakePhoto={() => {
          setShowModal(false);
          void pickFromCamera(handleUpload);
        }}
        onChooseFromGallery={() => {
          setShowModal(false);
          void pickFromGallery(handleUpload);
        }}
        onDelete={hasImage ? handleDelete : undefined}
        onClose={() => setShowModal(false)}
      />
    </>
  );
};
